import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useNavigation } from 'react-navi';

import {
  fetchUser as actionFetchUser,
  refreshUser as actionRefreshUser,
  resetUser as actionResetUser,
  insertUser as actionInsertUser,
  changeUserWallet as actionChangeUserWallet,
  deleteUserWallet as actionDeleteUserWallet,
  setAfterLoginRedirect as actionSetAfterLoginRedirect,
  resetAfterLoginRedirect as actionResetAfterLoginRedirect,
  updateUserReason as actionUpdateUserReason,
  fetchPlayerSettings as actionFetchPlayerSettings,
} from './actions';
import { User, Wallet } from '../../api/auth';
import { REASON_FOR_UPDATING_USER, userReducer, UserState } from './reducer';
import { PrimarySettingsContext } from '../primarySettings';
import { CommonContext } from '../common';

type AuthContextData = UserState;
interface AuthContextActions {
  fetchUser: (reasonForUpdatingUser: REASON_FOR_UPDATING_USER) => Promise<void>;
  insertUser: (
    user: User,
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER
  ) => Promise<void>;
  updateUserReason: (reason: REASON_FOR_UPDATING_USER) => Promise<void>;
  resetUser: () => Promise<void>;

  changeUserWallet: (currency: Wallet['currency']) => Promise<void>;
  deleteUserWallet: (currency: Wallet['currency']) => Promise<void>;

  setAfterLoginRedirect: (url: string) => void;
}

export const AuthContext = createContext<AuthContextData & AuthContextActions>(
  null as any
);

const initialState: UserState = {
  user: undefined,
  compPoints: undefined,
  reasonForUpdatingUser: undefined,
  isAuthenticated: false,
  pendingUser: true,
  wallets: [],
  extendedWallets: [],
  pendingWallets: false,
  afterLoginRedirectUrl: undefined,
  pendingPlayerSettings: true,
  playerSettings: undefined,
};

const USER_REFRESH_INTERVAL = 60e3;

export const AuthContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  const { updateDefaultUserStatuses } = useContext(PrimarySettingsContext);
  const { currentIp } = useContext(CommonContext);

  const navigation = useNavigation();

  const updateDefaultUserStatusesWithIp = useMemo(
    () => updateDefaultUserStatuses(currentIp?.countryCode),
    [updateDefaultUserStatuses, currentIp?.countryCode]
  );

  const fetchUser = useCallback(
    async (reasonForUpdatingUser: REASON_FOR_UPDATING_USER) => {
      return actionFetchUser(dispatch, {
        reasonForUpdatingUser,
        defaultState: initialState,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      });
    },
    [dispatch, updateDefaultUserStatusesWithIp]
  );

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const interval = setInterval(
      () =>
        actionRefreshUser(dispatch, {
          prevUser: state.user,
          updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
        }),
      USER_REFRESH_INTERVAL
    );
    return () => {
      clearInterval(interval);
    };
  }, [
    dispatch,
    state.isAuthenticated,
    state.user,
    updateDefaultUserStatusesWithIp,
  ]);

  const insertUser = useCallback(
    (user: User, reasonForUpdatingUser: REASON_FOR_UPDATING_USER) => {
      return actionInsertUser(dispatch, { user, reasonForUpdatingUser });
    },
    [dispatch]
  );

  const resetUser = useCallback(() => {
    return actionResetUser(dispatch, { navigation });
  }, [dispatch, navigation]);

  const updateUserReason = useCallback(
    (reason: REASON_FOR_UPDATING_USER) => {
      return actionUpdateUserReason(dispatch, {
        reasonForUpdatingUser: reason,
      });
    },
    [dispatch]
  );

  const changeUserWallet = useCallback(
    async (currency: Wallet['currency']) => {
      return actionChangeUserWallet(dispatch, {
        currency,
        defaultState: initialState,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      });
    },
    [dispatch, updateDefaultUserStatusesWithIp]
  );

  const deleteUserWallet = useCallback(
    async (currency: Wallet['currency']) => {
      await actionDeleteUserWallet(dispatch, {
        currency,
        defaultState: initialState,
        updateDefaultUserStatuses: updateDefaultUserStatusesWithIp,
      });

      if (currency === state.user?.currency) {
        changeUserWallet(
          state.wallets[0].currency === currency
            ? state.wallets[1].currency
            : state.wallets[0].currency
        );
      }
    },
    [
      dispatch,
      updateDefaultUserStatusesWithIp,
      changeUserWallet,
      state.user,
      state.wallets,
    ]
  );

  const setAfterLoginRedirect = useCallback(
    (url: string) => {
      actionSetAfterLoginRedirect(dispatch, { url });
    },
    [dispatch]
  );

  useEffect(() => {
    if (state.isAuthenticated === false || state.afterLoginRedirectUrl == null)
      return;

    window.location.href = state.afterLoginRedirectUrl;
    actionResetAfterLoginRedirect(dispatch, {});
  }, [dispatch, state.isAuthenticated, state.afterLoginRedirectUrl]);

  useEffect(() => {
    fetchUser(REASON_FOR_UPDATING_USER.VISIT);
  }, [fetchUser]);

  useEffect(() => {
    if (state.user == null || state.user.id == null) return;
  }, [state.user]);

  useEffect(() => {
    if (state.pendingUser) return;

    actionFetchPlayerSettings(dispatch, {});
  }, [state.pendingUser, state.isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        fetchUser,
        insertUser,
        resetUser,
        updateUserReason,
        changeUserWallet,
        deleteUserWallet,
        setAfterLoginRedirect,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
